import React from "react";
import { ImageProps } from "@/types/image";
import { Chip } from "../Chip";
import { cva, type VariantProps } from "class-variance-authority";
import { Image } from "../Image";
import Link from "next/link";

const caseCardVariants = cva(
  "group relative flex flex-col justify-end rounded-lg overflow-hidden cursor-pointer outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2",
  {
    variants: {
      color: {
        purple: "text-white bg-purple outline-purple",
        yellow: "text-black bg-yellow outline-yellow",
      },
      size: {
        sm: "aspect-3/4",
        lg: "w-full h-full",
      },
    },
    defaultVariants: {
      color: "purple",
      size: "sm",
    },
  }
);

interface CaseCardProps extends VariantProps<typeof caseCardVariants> {
  image?: ImageProps;
  title: string;
  chips?: string[];
  className?: string;
  href?: string;
}

export const CaseCard: React.FC<CaseCardProps> = ({
  color = "purple",
  size = "sm",
  image,
  title,
  chips = [],
  className = "",
  href,
}) => {
  // Force purple color when image is provided for better text contrast
  const effectiveColor = image ? "purple" : color;
  const chipColor = effectiveColor === "purple" ? "white" : "black";

  const content = (
    <>
      {image && (
        <>
          <Image
            {...image}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 70vw"
            className="absolute inset-0 w-full h-full object-cover scale-[1.025] group-hover:scale-[1] group-focus-visible:scale-[1] transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-linear-to-b from-black/0 from-50% to-black/90" />
        </>
      )}
      <div className="relative p-md flex flex-col gap-sm">
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-xs">
            {chips.map((chip, index) => (
              <Chip key={index} size="sm" color={chipColor}>
                {chip}
              </Chip>
            ))}
          </div>
        )}
        <p className="headline-4">{title}</p>
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={caseCardVariants({ color: effectiveColor, size, className })}
      >
        {content}
      </Link>
    );
  }

  return (
    <article
      className={caseCardVariants({ color: effectiveColor, size, className })}
    >
      {content}
    </article>
  );
};
